// Libraries
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useSession } from 'next-auth/react';
import { Fragment, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
// Components
import { LayoutHeader, LayoutSidebar, ModalWrapper } from '@organisms/index';
// Data
import { useModalStore } from '@stores/index';
import { config } from '@utils/env';
import { runDomainCanary } from '@utils/helpers';
// Types
import { Status } from '@globalTypes/index';
import { RootLayoutProps } from './RootLayout.type';

/**
 * Root layout component that provides a consistent layout structure for the application.
 * @param {RootLayoutProps} props - The props for the RootLayout component.
 * @param {ReactNode} props.children - The content to be rendered within the layout.
 * @returns {ReactElement} The RootLayout component.
 */
export const RootLayout = ({ children, center }: RootLayoutProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure({ id: 'layout' });
  const { status, data: sessionData } = useSession();
  const { isModalOpen, closeModal, modalBody = <Fragment></Fragment> } = useModalStore();
  const { env } = config;
  const showVersion = env.isLocal || env.isSandbox || env.isEphemeral || env.isStaging;
  const boxSx = center ? { display: 'flex', flex: '1' } : {};

  useEffect(() => {
    if (status === Status.authenticated) {
      const { user } = sessionData;
      Sentry.setUser({
        id: user?.sub,
        email: user?.email,
        username: user?.username,
      });
      // After setUser, so the probe's Sentry events carry the customer.
      void runDomainCanary();
    }
  }, [status, sessionData]);

  return (
    <Flex direction='column' minH='100vh' bg='backgrounds.default'>
      {status === Status.authenticated && <LayoutSidebar onClose={onClose} isOpen={isOpen} />}
      <LayoutHeader status={status} onOpen={onOpen} position='sticky' top={0} zIndex='docked' />
      <Box onClick={onClose} sx={boxSx}>
        {children}
      </Box>
      <ModalWrapper isModalOpen={isModalOpen} closeModal={closeModal}>
        {modalBody}
      </ModalWrapper>
      {showVersion && (
        <p style={{ position: 'fixed', bottom: '12px', left: '12px', opacity: 0.3 }}>
          {env.isLocal && `Local Env`}
          {env.isSandbox && `Build for hash ${env.versionTag}`}
          {env.isStaging && `Version: ${env.versionTag}`}
          {env.isEphemeral && (
            <>
              <span>Ephemeral env for </span>
              <a
                href={`https://github.com/dogpatchshipping/user_interface_service/pull/${env.versionTag}`}
                target='_blank'
                rel='noopener noreferrer'
                className='hover:underline'
              >
                {`https://github.com/dogpatchshipping/user_interface_service/pull/${env.versionTag}`}
              </a>
            </>
          )}
        </p>
      )}
    </Flex>
  );
};
